import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Section } from '~/components/layout';
import { Grid, Icon } from '~/components/ui';
import { cn } from '~/lib/cn';

const APY = 0.042;
const DURATION_OPTIONS = [1, 3, 5, 10, 15, 20];

// Chart drawing space (SVG user units). Axis labels live in HTML alongside the
// SVG, so this region maps 1:1 to the plotted data — the left edge is the first
// point ("Today") and the right edge is the final point.
const VIEW_W = 1000;
const VIEW_H = 340;
const PAD_T = 14; // top breathing room so the end dot isn't clipped
const PAD_B = 14;

const money = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const axisMoney = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatMoney = (v: number) => `$ ${money.format(Math.round(v))}`;
const formatAxis = (v: number) => `$${axisMoney.format(v)}`;

const dateFmt = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });

// Field label = 17sb, tightened tracking, 20px gap to its field, and left
// padding matching the input/select inner text padding (px-4) so the label
// aligns with the value typed inside the field.
const fieldLabelClass = 'mb-5 block pl-4 text-body font-semibold tracking-[-0.01em] text-grey-900';

// Shared field box: outlined (no fill), 20px radius, 72px tall, 1.5px border
// (border-box → no layout shift). The border rests at subtle black-6 and turns
// grey-1000 on focus, so focus is the active state without shifting layout.
const fieldBoxBase =
  'h-[72px] rounded-[20px] border-[1.5px] border-black-6 bg-transparent transition-colors';

/** A single projected point on the growth curve. */
interface Point {
  month: number;
  value: number;
  /** Friendly label for the scrub tooltip / axis ("Today", "Jul 2033"). */
  label: string;
}

const DAILY_RATE = APY / 365;

/**
 * Daily-compounding projection of a balance earning {@link APY} "paid daily".
 * Each calendar day the running balance grows by APY/365; the monthly
 * contribution is added once per month, on the month boundary. The compounding
 * math runs day-by-day (using each month's real day count) while the series is
 * sampled once per month to keep the chart smooth and cheap to render.
 * Returns one point per month, index 0 = today.
 */
function project(start: number, monthly: number, years: number): Point[] {
  const now = new Date();
  const totalMonths = years * 12;
  const points: Point[] = [{ month: 0, value: start, label: 'Today' }];
  let balance = start;
  for (let month = 1; month <= totalMonths; month++) {
    // Days in the calendar month we're advancing through (day 0 of the next
    // month = last day of this one).
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + month, 0).getDate();
    for (let day = 0; day < daysInMonth; day++) balance *= 1 + DAILY_RATE;
    balance += monthly;
    const date = new Date(now.getFullYear(), now.getMonth() + month, 1);
    points.push({ month, value: balance, label: dateFmt.format(date) });
  }
  return points;
}

/** Rounds a range up to a "nice" 1/2/5 * 10^n value for clean axis ticks. */
function niceNum(range: number, round: boolean) {
  const exp = Math.floor(Math.log10(range));
  const frac = range / 10 ** exp;
  let nice: number;
  if (round) {
    if (frac < 1.5) nice = 1;
    else if (frac < 3) nice = 2;
    else if (frac < 7) nice = 5;
    else nice = 10;
  } else {
    if (frac <= 1) nice = 1;
    else if (frac <= 2) nice = 2;
    else if (frac <= 5) nice = 5;
    else nice = 10;
  }
  return nice * 10 ** exp;
}

function niceScale(min: number, max: number, maxTicks = 5) {
  const range = niceNum(Math.max(max - min, 1), false);
  const step = niceNum(range / (maxTicks - 1), true);
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + step / 2; v += step) ticks.push(v);
  return { niceMin, niceMax, ticks };
}

/**
 * Rewards page hero calculator: three inputs + a live estimate driving a custom
 * inline-SVG area chart. Hovering / dragging across the chart scrubs to the
 * nearest month and surfaces the projected balance at that point.
 */
export function RewardsCalculator() {
  const [start, setStart] = useState(5000);
  const [monthly, setMonthly] = useState(1000);
  const [years, setYears] = useState(10);
  const [scrub, setScrub] = useState<number | null>(null);
  const [plotWidth, setPlotWidth] = useState(0);

  const plotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setPlotWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const points = useMemo(() => project(start, monthly, years), [start, monthly, years]);
  const n = points.length;

  // Everything derived from the projection (scales, axis ticks, and the two big
  // SVG path strings) is expensive to rebuild and depends ONLY on the plotted
  // data — never on the scrub position. Memoising it on `points` keeps scrubbing
  // (which fires setState on every frame) from re-running any of this work.
  const geometry = useMemo(() => {
    const values = points.map((p) => p.value);
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    const { niceMin, niceMax, ticks } = niceScale(dataMin, dataMax);
    const span = niceMax - niceMin || 1;

    const xFor = (i: number) => (i / (n - 1)) * VIEW_W;
    const yFor = (v: number) => PAD_T + (1 - (v - niceMin) / span) * (VIEW_H - PAD_T - PAD_B);

    const linePath = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(1)},${yFor(p.value).toFixed(1)}`)
      .join(' ');
    const areaPath = `${linePath} L${VIEW_W},${VIEW_H} L0,${VIEW_H} Z`;

    const xLabelPct = (i: number) => (i / (n - 1)) * 100;
    const yLabelPct = (v: number) => (yFor(v) / VIEW_H) * 100;

    return { ticks, xFor, yFor, linePath, areaPath, xLabelPct, yLabelPct };
  }, [points, n]);
  const { ticks, xFor, yFor, linePath, areaPath, xLabelPct, yLabelPct } = geometry;

  // X-axis year ticks — spaced so labels never collide. The step grows on
  // narrower charts (measured via ResizeObserver) so mobile shows fewer labels.
  const yearTicks = useMemo(() => {
    const maxLabels = Math.max(2, Math.floor((plotWidth || 640) / 72));
    const yearStep = Math.max(1, Math.ceil((years + 1) / maxLabels));
    const out: number[] = [];
    for (let y = 0; y <= years; y += yearStep) out.push(y);
    const lastTick = out[out.length - 1];
    if (lastTick !== years) {
      // Avoid crowding the final label against the previous tick.
      if (years - lastTick < yearStep / 2) out.pop();
      out.push(years);
    }
    return out;
  }, [years, plotWidth]);

  // The static chart layers (gridlines + area + line) depend only on `geometry`,
  // so memoising the element lets React skip reconciling the two large paths on
  // every scrub frame — only the lightweight overlay below moves.
  const chartLayers = useMemo(
    () => (
      <>
        {/* Horizontal gridlines at each y tick */}
        {ticks.map((t) => (
          <line
            key={t}
            x1="0"
            x2={VIEW_W}
            y1={yFor(t)}
            y2={yFor(t)}
            stroke="var(--color-black-6)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        <path d={areaPath} fill="url(#rewardsFill)" />
        <path
          d={linePath}
          fill="none"
          stroke="var(--color-green)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </>
    ),
    [ticks, yFor, areaPath, linePath],
  );

  const activeIndex = scrub ?? n - 1;
  const activePoint = points[activeIndex];
  const isScrubbing = scrub !== null;

  // Total rewards EARNED up to the scrubbed point = balance there minus the money
  // put in by then (initial deposit + one contribution per elapsed month). This is
  // the cumulative yield, distinct from the "Estimate result" total balance.
  const contributionsToDate = start + monthly * activePoint.month;
  const rewardsEarned = Math.max(0, activePoint.value - contributionsToDate);

  // Scrub plumbing: pointermove can fire many times per frame, so we cache the
  // client X and flush at most once per animation frame. The plot rect is cached
  // on interaction start (pointer enter/down) instead of measured every move,
  // avoiding layout reads in the hot path.
  const rectRef = useRef<DOMRect | null>(null);
  const frameRef = useRef<number | null>(null);
  const clientXRef = useRef(0);

  const flushScrub = useCallback(() => {
    frameRef.current = null;
    const rect = rectRef.current ?? plotRef.current?.getBoundingClientRect();
    if (!rect) return;
    rectRef.current = rect;
    const rel = (clientXRef.current - rect.left) / rect.width;
    const index = Math.round(rel * (n - 1));
    setScrub(Math.max(0, Math.min(n - 1, index)));
  }, [n]);

  const scheduleScrub = useCallback(
    (clientX: number) => {
      clientXRef.current = clientX;
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(flushScrub);
    },
    [flushScrub],
  );

  const captureRect = useCallback(() => {
    const el = plotRef.current;
    if (el) rectRef.current = el.getBoundingClientRect();
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      scheduleScrub(e.clientX);
    },
    [scheduleScrub],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      captureRect();
      scheduleScrub(e.clientX);
    },
    [captureRect, scheduleScrub],
  );

  const endScrub = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    rectRef.current = null;
    setScrub(null);
  }, []);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return (
    <Section className="pt-[104px] md:pt-[120px]">
      <Grid className="gap-y-0">
        {/* Inputs row + live estimate — aligned to the site's 9-column grid.
            Nested Grid so the fields snap to the same columns as the chart. */}
        <div className="col-span-9">
          <Grid className="items-start gap-y-5 max-md:grid-cols-2">
            <NumberField
              label="Start with"
              value={start}
              onChange={setStart}
              className="col-span-9 min-w-0 max-md:col-span-1 lg:col-span-3"
            />
            <NumberField
              label="Each month I put aside"
              value={monthly}
              onChange={setMonthly}
              className="col-span-9 min-w-0 max-md:col-span-1 lg:col-span-3 lg:col-start-4"
            />
            <div className="col-span-9 max-md:col-span-2 lg:col-span-1 lg:col-start-7">
              <span className={fieldLabelClass}>Duration</span>
              <div className="relative">
                <select
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  aria-label="Duration in years"
                  className={cn(
                    fieldBoxBase,
                    'w-full cursor-pointer appearance-none pr-9 pl-4 text-body font-semibold outline-none focus:border-grey-1000',
                  )}>
                  {DURATION_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y} {y === 1 ? 'Year' : 'Years'}
                    </option>
                  ))}
                </select>
                <Icon
                  name="chevron-down"
                  className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 opacity-60"
                />
              </div>
            </div>
            <div className="col-span-9 max-md:col-span-2 lg:col-span-2 lg:col-start-8">
              {/* No grey box on the estimate — transparent, right-aligned to the
                  grid's right edge. Keeps the 72px row height + gap under value. */}
              <span className="mb-3 block pl-4 text-body font-semibold tracking-[-0.01em] text-grey-900 lg:pl-0 lg:text-right">
                Estimate result
              </span>
              <div className="flex h-[72px] items-center px-4 pb-3 lg:justify-end lg:px-0">
                <p className="text-h4 font-semibold whitespace-nowrap [text-box-trim:none] md:text-h3">
                  {formatMoney(activePoint.value)}
                </p>
              </div>
            </div>
          </Grid>
        </div>

        {/* Chart: HTML y-axis labels + inline-SVG area chart + HTML x-axis labels */}
        <div className="col-span-9 mt-12 flex gap-3">
          <div className="relative hidden w-12 shrink-0 sm:block" aria-hidden>
            {ticks.map((t) => (
              <span
                key={t}
                style={{ top: `${yLabelPct(t)}%` }}
                className="absolute right-0 -translate-y-1/2 text-note tracking-[-0.01em] text-grey-900 tabular-nums">
                {formatAxis(t)}
              </span>
            ))}
          </div>

          <div className="min-w-0 flex-1">
            <div
              ref={plotRef}
              className="relative w-full touch-none select-none"
              style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}` }}
              onPointerEnter={captureRect}
              onPointerMove={onPointerMove}
              onPointerDown={onPointerDown}
              onPointerUp={endScrub}
              onPointerLeave={endScrub}
              onPointerCancel={endScrub}>
              <svg
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                preserveAspectRatio="none"
                className="absolute inset-0 h-full w-full overflow-visible"
                role="img"
                aria-label={`Projected balance growth over ${years} ${years === 1 ? 'year' : 'years'}, ending at ${formatMoney(points[n - 1].value)}`}>
                <defs>
                  <linearGradient id="rewardsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-green)" stopOpacity="0.28" />
                    <stop offset="100%" stopColor="var(--color-green)" stopOpacity="0" />
                  </linearGradient>
                </defs>

                {chartLayers}

                {/* Scrub vertical guide */}
                {isScrubbing && (
                  <line
                    x1={xFor(activeIndex)}
                    x2={xFor(activeIndex)}
                    y1={PAD_T}
                    y2={VIEW_H}
                    stroke="var(--color-green)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                    vectorEffect="non-scaling-stroke"
                  />
                )}

                {/* Active point marker (end dot by default, scrub dot while scrubbing) */}
                <g>
                  <circle
                    cx={xFor(activeIndex)}
                    cy={yFor(activePoint.value)}
                    r="9"
                    fill="var(--color-green)"
                    fillOpacity="0.18"
                  />
                  <circle
                    cx={xFor(activeIndex)}
                    cy={yFor(activePoint.value)}
                    r="5"
                    fill="var(--color-green)"
                    stroke="white"
                    strokeWidth="2"
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              </svg>

              {/* Scrub tooltip */}
              {isScrubbing && (
                <div
                  className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-2xl bg-white px-5 py-3 text-center whitespace-nowrap text-grey-900 shadow-[0_12px_40px_rgba(0,0,0,0.10),0_4px_16px_rgba(0,0,0,0.06)] motion-safe:transition-[left,top] motion-safe:duration-75"
                  style={{
                    left: `${Math.min(92, Math.max(8, xLabelPct(activeIndex)))}%`,
                    top: `calc(${yLabelPct(activePoint.value)}% - 14px)`,
                  }}>
                  <span className="block text-body-m font-medium tracking-[-0.01em]">
                    {activePoint.label}
                  </span>
                  <span className="mt-2 block text-note tracking-[-0.01em] text-grey-900">
                    Rewards earned{' '}
                    <span className="font-semibold text-green">
                      +${money.format(Math.round(rewardsEarned))}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* X-axis labels aligned to data positions */}
            <div className="relative mt-3 h-4" aria-hidden>
              {yearTicks.map((y) => {
                const i = y * 12;
                return (
                  <span
                    key={y}
                    style={{ left: `${xLabelPct(i)}%` }}
                    className={cn(
                      'absolute top-0 -translate-x-1/2 text-note tracking-[-0.01em] text-grey-900 whitespace-nowrap',
                      y === 0 && 'translate-x-0',
                      y === years && '-translate-x-full',
                    )}>
                    {y === 0 ? 'Today' : `${y} ${y === 1 ? 'Year' : 'Years'}`}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <p className="col-span-9 mt-8 text-body-s tracking-[-0.01em] text-grey-500">
          Estimated returns are illustrative only and not guaranteed. Reward rates are variable,
          subject to change, and may differ based on your balance and currency held. Calculator
          projections assume a constant rate and do not account for fees, taxes, or market
          fluctuations.
        </p>
      </Grid>
    </Section>
  );
}

function NumberField({
  label,
  value,
  onChange,
  className,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  className?: string;
}) {
  return (
    <label className={cn('block', className)}>
      <span className={fieldLabelClass}>{label}</span>
      <span className={cn(fieldBoxBase, 'flex items-center px-4 focus-within:border-grey-1000')}>
        <input
          type="text"
          inputMode="numeric"
          value={money.format(value)}
          onChange={(e) => {
            const digits = e.target.value.replace(/[^0-9]/g, '');
            onChange(digits === '' ? 0 : Number(digits));
          }}
          className="w-full bg-transparent text-body font-medium tabular-nums outline-none"
        />
      </span>
    </label>
  );
}
